import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  TrendingUp,
  Heart,
  Eye,
  Building2,
  Bandage,
  HeartPulse,
  Shield,
  Umbrella,
  Handshake,
  Zap,
  ArrowRight,
  Users,
  Percent,
  RotateCcw,
  ChevronDown,
  ChevronUp,
  Copy,
  Check,
  Layers,
  FileDown

} from "lucide-react";
import { Slider } from "@/components/ui/slider";
import Seo from "@/components/Seo";
import { 
  CalculatorLayout, 
  CalculatorCard, 
  CalculatorInput,
  CalculatorLabel,
  useCalculatorTheme 
} from "@/components/calculator";

interface SimpleProduct {
  id: string;
  name: string;
  icon: React.ComponentType<{ className?: string }>;
  avgPremium: number; // Average annual premium
  penetrationRate: number;
  year1CommissionPct: number;
  renewalCommissionPct: number;
  enabled: boolean;
}

const defaultProducts: SimpleProduct[] = [
  { id: "dental", name: "Dental", icon: Heart, avgPremium: 480, penetrationRate: 25, year1CommissionPct: 20, renewalCommissionPct: 15, enabled: true },
  { id: "vision", name: "Vision", icon: Eye, avgPremium: 300, penetrationRate: 25, year1CommissionPct: 20, renewalCommissionPct: 15, enabled: true },
  { id: "hospital", name: "Hospital Indemnity", icon: Building2, avgPremium: 600, penetrationRate: 20, year1CommissionPct: 20, renewalCommissionPct: 15, enabled: true },
  { id: "accident", name: "Accident", icon: Bandage, avgPremium: 480, penetrationRate: 20, year1CommissionPct: 20, renewalCommissionPct: 15, enabled: true },
  { id: "critical", name: "Critical Illness", icon: HeartPulse, avgPremium: 720, penetrationRate: 15, year1CommissionPct: 20, renewalCommissionPct: 15, enabled: false },
  { id: "alternative", name: "Alternative Solutions", icon: Shield, avgPremium: 1000, penetrationRate: 10, year1CommissionPct: 25, renewalCommissionPct: 20, enabled: false },
  { id: "life", name: "Life Insurance", icon: Umbrella, avgPremium: 1800, penetrationRate: 10, year1CommissionPct: 50, renewalCommissionPct: 5, enabled: false },
  { id: "final", name: "Final Expense", icon: Handshake, avgPremium: 1200, penetrationRate: 10, year1CommissionPct: 80, renewalCommissionPct: 5, enabled: false },
];

const CrossSellSimpleContent = () => {
  const { isDark } = useCalculatorTheme();
  
  const [totalClients, setTotalClients] = useState(150);
  const [products, setProducts] = useState<SimpleProduct[]>(defaultProducts);
  const [showInactiveProducts, setShowInactiveProducts] = useState(false);
  const [currentRevenue, setCurrentRevenue] = useState(0);
  const [copied, setCopied] = useState(false);

  const resetAll = () => {
    setTotalClients(150);
    setProducts(defaultProducts);
    setShowInactiveProducts(false);
    setCurrentRevenue(0);
  };

  const applyMix = (ids: string[]) => {
    setProducts(prev => prev.map(p => ({ ...p, enabled: ids.includes(p.id) })));
  };

  const productMixes: { label: string; ids: string[] }[] = [
    { label: "Dental + Vision", ids: ["dental", "vision"] },
    { label: "Core Bundle", ids: ["dental", "vision", "hospital", "accident"] },
    { label: "Worksite Suite", ids: ["hospital", "accident", "critical"] },
    { label: "Everything", ids: defaultProducts.map(p => p.id) },
  ];


  const toggleProduct = (id: string) => {
    setProducts(prev => prev.map(p => 
      p.id === id ? { ...p, enabled: !p.enabled } : p
    ));
  };

  const updateProductPremium = (id: string, premium: number) => {
    setProducts(prev => prev.map(p => 
      p.id === id ? { ...p, avgPremium: premium } : p
    ));
  };

  const updateProductCommission = (id: string, field: 'year1CommissionPct' | 'renewalCommissionPct' | 'penetrationRate', value: number) => {
    setProducts(prev => prev.map(p => 
      p.id === id ? { ...p, [field]: value } : p
    ));
  };

  const enabledProducts = products.filter(p => p.enabled);
  
  const calculations = useMemo(() => {
    const productRevenues = enabledProducts.map(p => {
      // policies = clients x penetration rate
      const policies = Math.round(totalClients * (p.penetrationRate / 100));
      // commission per policy = annual premium x commission %
      const year1Commission = p.avgPremium * (p.year1CommissionPct / 100);
      const renewalCommission = p.avgPremium * (p.renewalCommissionPct / 100);
      return {
        ...p,
        policies,
        year1Commission,
        renewalCommission,
        year1Revenue: Math.round(policies * year1Commission),
        renewalRevenue: Math.round(policies * renewalCommission),
      };
    });

    const totalYear1Revenue = productRevenues.reduce((sum, p) => sum + p.year1Revenue, 0);
    const totalRenewalRevenue = productRevenues.reduce((sum, p) => sum + p.renewalRevenue, 0);
    const totalPolicies = productRevenues.reduce((sum, p) => sum + p.policies, 0);

    return {
      productRevenues,
      totalYear1Revenue,
      totalRenewalRevenue,
      totalPolicies,
    };
  }, [totalClients, enabledProducts]);


  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD', 
      minimumFractionDigits: 0, 
      maximumFractionDigits: 0 
    }).format(value);
  };

  const avgPenetration = enabledProducts.length
    ? Math.round(enabledProducts.reduce((s, p) => s + p.penetrationRate, 0) / enabledProducts.length)
    : 0;

  const year1PerClient = totalClients > 0 ? calculations.totalYear1Revenue / totalClients : 0;
  const renewalPerClient = totalClients > 0 ? calculations.totalRenewalRevenue / totalClients : 0;

  const revenueLift = currentRevenue > 0
    ? Math.round((calculations.totalRenewalRevenue / currentRevenue) * 100)
    : 0;


  const summaryText = [
    `Chirpwell Cross-Sell Projection`,
    ``,
    `Book size: ${totalClients} clients`,
    `Products: ${enabledProducts.map(p => p.name).join(", ") || "none selected"}`,
    `Average penetration: ${avgPenetration}%`,
    `Ancillary policies: ${calculations.totalPolicies}`,
    ``,
    `1st Year Revenue: ${formatCurrency(calculations.totalYear1Revenue)} (${formatCurrency(calculations.totalYear1Revenue / 12)}/mo)`,
    `Renewal Revenue (Year 2+): ${formatCurrency(calculations.totalRenewalRevenue)} (${formatCurrency(calculations.totalRenewalRevenue / 12)}/mo)`,
    `Revenue per client: ${formatCurrency(year1PerClient)} year 1 / ${formatCurrency(renewalPerClient)} renewal`,
    ...(currentRevenue > 0
      ? [``, `Current annual revenue: ${formatCurrency(currentRevenue)}`, `Ongoing lift from ancillary: +${revenueLift}%`]
      : []),
  ].join("\n");

  const copySummary = async () => {
    try {
      await navigator.clipboard.writeText(summaryText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const exportPDF = async () => {
    const { default: jsPDF } = await import("jspdf");
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    const W = doc.internal.pageSize.getWidth();
    let y = 56;

    doc.setFillColor(15, 23, 42);
    doc.rect(0, 0, W, 90, "F");
    doc.setTextColor(251, 191, 36);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.text("Chirpwell Cross-Sell Projection", 40, 50);
    doc.setTextColor(226, 232, 240);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.text(new Date().toLocaleDateString(), 40, 70);

    y = 130;
    doc.setTextColor(30, 41, 59);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text("Book Overview", 40, y);
    y += 20;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    [
      `Client book size: ${totalClients}`,
      `Products sold: ${enabledProducts.map(p => p.name).join(", ") || "none selected"}`,
      `Average penetration: ${avgPenetration}%`,
      `Ancillary policies: ${calculations.totalPolicies}`,
    ].forEach(line => { doc.text(line, 40, y); y += 16; });

    y += 14;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text("Revenue Potential", 40, y);
    y += 20;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    [
      `1st Year Revenue: ${formatCurrency(calculations.totalYear1Revenue)}  (${formatCurrency(calculations.totalYear1Revenue / 12)}/mo)`,
      `Renewal Revenue (Year 2+): ${formatCurrency(calculations.totalRenewalRevenue)}  (${formatCurrency(calculations.totalRenewalRevenue / 12)}/mo)`,
      `Revenue per client: ${formatCurrency(year1PerClient)} year 1 / ${formatCurrency(renewalPerClient)} renewal`,
      ...(currentRevenue > 0
        ? [
            `Current annual revenue: ${formatCurrency(currentRevenue)}`,
            `Ongoing lift from ancillary: +${revenueLift}%`,
            `New book total: ${formatCurrency(currentRevenue + calculations.totalRenewalRevenue)}/yr`,
          ]
        : []),
    ].forEach(line => { doc.text(line, 40, y); y += 16; });

    y += 14;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text("Product Breakdown", 40, y);
    y += 22;
    doc.setFontSize(10);
    doc.text("Product", 40, y);
    doc.text("Policies", 300, y, { align: "right" });
    doc.text("Year 1", 400, y, { align: "right" });
    doc.text("Renewal", 500, y, { align: "right" });
    y += 6;
    doc.setDrawColor(203, 213, 225);
    doc.line(40, y, 500, y);
    y += 16;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    calculations.productRevenues.forEach(p => {
      doc.text(p.name, 40, y);
      doc.text(String(p.policies), 300, y, { align: "right" });
      doc.text(formatCurrency(p.year1Revenue), 400, y, { align: "right" });
      doc.text(formatCurrency(p.renewalRevenue), 500, y, { align: "right" });
      y += 16;
    });
    doc.setDrawColor(203, 213, 225);
    doc.line(40, y - 8, 500, y - 8);
    doc.setFont("helvetica", "bold");
    doc.text("Total", 40, y + 6);
    doc.text(String(calculations.totalPolicies), 300, y + 6, { align: "right" });
    doc.text(formatCurrency(calculations.totalYear1Revenue), 400, y + 6, { align: "right" });
    doc.text(formatCurrency(calculations.totalRenewalRevenue), 500, y + 6, { align: "right" });

    y += 46;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text("Next Steps", 40, y);
    y += 20;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    [
      "1. Review the product mix and penetration assumptions with your team.",
      "2. Confirm carrier commission rates for each ancillary product.",
      "3. Build a client outreach plan targeting the highest-value products first.",
    ].forEach(line => { doc.text(line, 40, y); y += 16; });

    doc.setFontSize(9);
    doc.setTextColor(120, 130, 145);
    doc.text(
      "Estimates only. Policies = clients x penetration rate. Revenue = policies x annual premium x commission %.",
      40,
      doc.internal.pageSize.getHeight() - 40
    );

    doc.save("chirpwell-cross-sell-projection.pdf");
  };




  // Theme-aware colors
  const colors = {
    cardBg: isDark 
      ? "rgba(255,255,255,0.03)" 
      : "rgba(255,255,255,0.8)",
    cardBorder: isDark 
      ? "rgba(255,255,255,0.1)" 
      : "rgba(0,0,0,0.1)",
    textPrimary: isDark ? "text-white" : "text-slate-900",
    textSecondary: isDark ? "text-white/70" : "text-slate-700",
    textMuted: isDark ? "text-white/50" : "text-slate-600",

    accentBg: isDark
      ? "rgba(16,185,129,0.15)"
      : "rgba(16,185,129,0.1)",
    accentBorder: isDark
      ? "rgba(16,185,129,0.4)"
      : "rgba(16,185,129,0.3)",
    productBgActive: isDark
      ? "rgba(251,191,36,0.15)"
      : "rgba(251,191,36,0.1)",
    productBorderActive: isDark
      ? "rgba(251,191,36,0.4)"
      : "rgba(251,191,36,0.3)",
    productBgInactive: isDark
      ? "rgba(255,255,255,0.03)"
      : "rgba(0,0,0,0.02)",
    productBorderInactive: isDark
      ? "rgba(255,255,255,0.1)"
      : "rgba(0,0,0,0.1)",
  };

  return (
    <div className="max-w-5xl mx-auto">
      {/* Hero Result - The Big Number */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="rounded-3xl overflow-hidden mb-10"
        style={{
          background: isDark 
            ? "linear-gradient(135deg, rgba(16,185,129,0.2) 0%, rgba(5,150,105,0.15) 100%)"
            : "linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(5,150,105,0.1) 100%)",
          border: `3px solid ${colors.accentBorder}`,
          boxShadow: isDark 
            ? "0 25px 50px -12px rgba(16,185,129,0.25), inset 0 1px 0 rgba(255,255,255,0.1)"
            : "0 25px 50px -12px rgba(16,185,129,0.2)",
        }}
      >
        <div className="p-5 sm:p-8 md:p-12 text-center">
          <motion.p 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className={`text-lg md:text-xl uppercase tracking-widest mb-4 font-semibold ${colors.textSecondary}`}
          >
            Your Cross-Sell Revenue Potential
          </motion.p>
          
          {/* Year 1 and Renewal Revenue - Side by Side */}
          <div className="flex flex-col md:flex-row justify-center items-center gap-6 md:gap-12">
            <motion.div
              key={`year1-${calculations.totalYear1Revenue}`}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 200, damping: 20 }}
              className="text-center"
            >
              <p className={`text-sm md:text-base uppercase tracking-wider mb-2 font-semibold ${colors.textMuted}`}>
                1st Year Revenue
              </p>
              <p
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black break-words"
                style={{
                  color: isDark ? "#fbbf24" : "#b45309",
                  textShadow: isDark ? "0 0 40px rgba(251,191,36,0.4)" : "none",
                }}
              >
                {formatCurrency(calculations.totalYear1Revenue)}
              </p>

            </motion.div>
            
            <div className={`hidden md:block w-px h-20 ${isDark ? "bg-white/20" : "bg-slate-300"}`} />
            
            <motion.div
              key={`renewal-${calculations.totalRenewalRevenue}`}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 200, damping: 20, delay: 0.1 }}
              className="text-center"
            >
              <p className={`text-sm md:text-base uppercase tracking-wider mb-2 font-semibold ${colors.textMuted}`}>
                Renewal Revenue (Year 2+)

              </p>
              <p
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black break-words"
                style={{
                  color: isDark ? "#34d399" : "#047857",
                  textShadow: isDark ? "0 0 40px rgba(16,185,129,0.4)" : "none",
                }}
              >
                {formatCurrency(calculations.totalRenewalRevenue)}
              </p>

            </motion.div>
          </div>
          
          {/* Monthly Breakdown Subsection */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="flex flex-col md:flex-row justify-center items-center gap-4 md:gap-10 mt-6"
          >
            <div className="flex flex-wrap items-center justify-center gap-2">
              <span className={`text-sm uppercase tracking-wider font-medium ${colors.textMuted}`}>Monthly:</span>
              <span className={`text-xl md:text-2xl font-bold ${isDark ? "text-amber-400/80" : "text-amber-600/80"}`}>
                {formatCurrency(calculations.totalYear1Revenue / 12)}
              </span>
              <span className={`text-xs uppercase ${colors.textMuted}`}>/mo (Year 1)</span>
            </div>
            <div className={`hidden md:block w-px h-6 ${isDark ? "bg-white/20" : "bg-slate-300"}`} />
            <div className="flex items-center gap-2">
              <span className={`text-xl md:text-2xl font-bold ${isDark ? "text-emerald-400/80" : "text-emerald-600/80"}`}>
                {formatCurrency(calculations.totalRenewalRevenue / 12)}
              </span>
              <span className={`text-xs uppercase ${colors.textMuted}`}>/mo (Renewal)</span>
            </div>
          </motion.div>
          
          {/* Policies Sold - Simple display */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-8 pt-8 border-t-2 border-current/10"
          >
            <div className="flex justify-center items-baseline gap-3">
              <motion.span
                key={calculations.totalPolicies}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className={`text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black ${isDark ? "text-amber-400" : "text-amber-600"}`}
              >
                {calculations.totalPolicies}
              </motion.span>
              <span className={`text-xl md:text-2xl font-semibold ${colors.textMuted}`}>
                ancillary policies
              </span>
            </div>
            <p className={`text-center text-base md:text-lg mt-3 ${colors.textSecondary}`}>
              across <span className={`font-bold ${isDark ? "text-amber-400" : "text-amber-600"}`}>{enabledProducts.length}</span> products
            </p>
          </motion.div>

          {/* Revenue Per Client */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.32 }}
            className="mt-8 pt-8 border-t-2 border-current/10"
          >
            <p className={`text-sm md:text-base uppercase tracking-widest font-semibold mb-4 ${colors.textMuted}`}>
              Revenue Per Client
            </p>
            <div className="flex flex-col md:flex-row justify-center items-center gap-6 md:gap-12">
              <div className="text-center">
                <p className={`text-3xl sm:text-4xl md:text-5xl font-black ${isDark ? "text-amber-400" : "text-amber-700"}`}>
                  {formatCurrency(year1PerClient)}
                </p>
                <p className={`text-xs md:text-sm uppercase tracking-wider mt-1 font-semibold ${colors.textMuted}`}>
                  per client - year 1
                </p>
              </div>
              <div className={`hidden md:block w-px h-14 ${isDark ? "bg-white/20" : "bg-slate-300"}`} />
              <div className="text-center">
                <p className={`text-3xl sm:text-4xl md:text-5xl font-black ${isDark ? "text-emerald-400" : "text-emerald-700"}`}>
                  {formatCurrency(renewalPerClient)}
                </p>
                <p className={`text-xs md:text-sm uppercase tracking-wider mt-1 font-semibold ${colors.textMuted}`}>
                  per client - renewal
                </p>
              </div>
            </div>
          </motion.div>

          {/* Live "what if" summary line */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.35 }}
            className={`mt-8 text-base md:text-xl font-medium ${colors.textSecondary}`}
          >
            If you sell <span className={`font-bold ${isDark ? "text-amber-400" : "text-amber-700"}`}>{enabledProducts.length}</span> products
            to <span className={`font-bold ${isDark ? "text-amber-400" : "text-amber-700"}`}>{avgPenetration}%</span> of your{" "}
            <span className={`font-bold ${isDark ? "text-amber-400" : "text-amber-700"}`}>{totalClients}</span> clients, you earn{" "}
            <span className={`font-bold ${isDark ? "text-amber-400" : "text-amber-700"}`}>{formatCurrency(calculations.totalYear1Revenue)}</span> in year 1 and{" "}
            <span className={`font-bold ${isDark ? "text-emerald-400" : "text-emerald-700"}`}>{formatCurrency(calculations.totalRenewalRevenue)}</span> every year after.
          </motion.p>

          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <button
              onClick={copySummary}
              className={`inline-flex items-center gap-2 px-5 py-3 rounded-xl text-base font-bold transition-all hover:scale-105 ${
                isDark
                  ? "bg-white/10 text-white/80 border-2 border-white/20 hover:bg-white/15"
                  : "bg-white text-slate-700 border-2 border-slate-200 hover:bg-slate-50"
              }`}
            >
              {copied ? <Check size={18} /> : <Copy size={18} />}
              {copied ? "Copied to clipboard" : "Copy summary for email"}
            </button>
            <button
              onClick={exportPDF}
              className={`inline-flex items-center gap-2 px-5 py-3 rounded-xl text-base font-bold transition-all hover:scale-105 ${
                isDark
                  ? "bg-amber-500/20 text-amber-300 border-2 border-amber-500/40 hover:bg-amber-500/30"
                  : "bg-amber-100 text-amber-800 border-2 border-amber-400 hover:bg-amber-200"
              }`}
            >
              <FileDown size={18} />
              Export PDF
            </button>
          </div>

        </div>
      </motion.div>

      {/* Simple ROI: current revenue vs ancillary lift */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="mb-10"
      >
        <CalculatorCard
          title="Book Yield (Optional)"
          description="Enter your current annual commission revenue to see the lift"
        >
          <div className="flex flex-col md:flex-row items-center justify-center gap-8">
            <div className="flex flex-col items-center gap-2">
              <CalculatorLabel>Current Annual Revenue</CalculatorLabel>
              <CalculatorInput
                type="text"
                inputMode="numeric"
                value={currentRevenue ? currentRevenue.toLocaleString() : ""}
                onChange={(e) => setCurrentRevenue(parseInt(e.target.value.replace(/[^0-9]/g, "")) || 0)}
                className="text-2xl md:text-3xl font-black h-16 max-w-xs text-center"
                placeholder="$0"
              />
            </div>
            {currentRevenue > 0 && (
              <>
                <div className={`hidden md:block w-px h-16 ${isDark ? "bg-white/20" : "bg-slate-300"}`} />
                <div className="text-center">
                  <p className={`text-sm uppercase tracking-wider font-semibold mb-1 ${colors.textMuted}`}>Ongoing Revenue Lift</p>
                  <p className={`text-4xl md:text-5xl font-black ${isDark ? "text-emerald-400" : "text-emerald-600"}`}>
                    +{revenueLift}%
                  </p>
                  <p className={`text-sm mt-1 ${colors.textSecondary}`}>
                    New book total: {formatCurrency(currentRevenue + calculations.totalRenewalRevenue)}/yr
                  </p>
                </div>
              </>
            )}
          </div>
        </CalculatorCard>
      </motion.div>



      {/* Client Count */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="mb-10"
      >
        <CalculatorCard
          title="Total Client Book Size"
        >
          <div className="flex flex-col items-center space-y-6">
            <div className="flex items-center justify-center gap-6">
              <Users className={`h-12 w-12 ${isDark ? "text-amber-400" : "text-amber-600"}`} />
              <CalculatorInput
                type="number"
                value={totalClients || ""}
                onChange={(e) => setTotalClients(parseInt(e.target.value) || 0)}
                className="text-4xl md:text-5xl font-black h-20 max-w-xs text-center"
                placeholder="150"
              />
            </div>
            <div className="flex gap-3 flex-wrap justify-center">
              {[50, 100, 150, 200, 300, 500, 1000].map(val => (
                <button
                  key={val}
                  onClick={() => setTotalClients(val)}
                  className={`px-5 py-3 rounded-xl text-lg font-bold transition-all transform hover:scale-105 ${
                    totalClients === val
                      ? isDark 
                        ? "bg-amber-500/30 text-amber-300 border-2 border-amber-500/60 shadow-lg shadow-amber-500/20" 
                        : "bg-amber-100 text-amber-700 border-2 border-amber-400 shadow-lg shadow-amber-500/20"
                      : isDark
                        ? "bg-white/5 text-white/70 border-2 border-white/10 hover:bg-white/10"
                        : "bg-slate-100 text-slate-600 border-2 border-slate-200 hover:bg-slate-200"
                  }`}
                >
                  {val}
                </button>
              ))}
            </div>
          </div>
        </CalculatorCard>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <CalculatorCard
          title="Ancillary Products"
          description="Configure each product's settings and penetration rate"
        >
          {/* Product mix presets */}
          <div className="flex flex-wrap items-center justify-center gap-3 mb-6">
            <span className={`flex items-center gap-2 text-sm font-semibold uppercase tracking-wider ${colors.textMuted}`}>
              <Layers size={16} /> Product Mix
            </span>
            {productMixes.map(mix => {
              const active =
                enabledProducts.length === mix.ids.length &&
                enabledProducts.every(p => mix.ids.includes(p.id));
              return (
                <button
                  key={mix.label}
                  onClick={() => applyMix(mix.ids)}
                  className={`px-4 py-2 rounded-xl text-base font-bold transition-all hover:scale-105 ${
                    active
                      ? isDark
                        ? "bg-amber-500/30 text-amber-300 border-2 border-amber-500/60"
                        : "bg-amber-100 text-amber-700 border-2 border-amber-400"
                      : isDark
                        ? "bg-white/5 text-white/70 border-2 border-white/10 hover:bg-white/10"
                        : "bg-slate-100 text-slate-600 border-2 border-slate-200 hover:bg-slate-200"
                  }`}
                >
                  {mix.label}
                </button>
              );
            })}
          </div>

          {/* Active Products */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {products.filter(p => p.enabled).map((product, index) => {
              const Icon = product.icon;
              const productCalc = calculations.productRevenues.find(p => p.id === product.id);
              
              const policies = productCalc?.policies || Math.round(totalClients * (product.penetrationRate / 100));
              
              return (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * index }}
                  whileHover={{ scale: 1.02 }}
                  className="p-4 md:p-6 rounded-2xl transition-all duration-300 overflow-hidden"
                  style={{
                    background: isDark 
                      ? "linear-gradient(135deg, rgba(251,191,36,0.2) 0%, rgba(245,158,11,0.15) 100%)"
                      : "linear-gradient(135deg, rgba(251,191,36,0.15) 0%, rgba(245,158,11,0.1) 100%)",
                    border: `2px solid ${colors.productBorderActive}`,
                    boxShadow: isDark ? "0 10px 40px -10px rgba(251,191,36,0.3)" : "none",
                  }}
                >
                  {/* Header row with toggle and name */}
                  <div className="flex items-center mb-5">
                    <button
                      onClick={() => toggleProduct(product.id)}
                      className="flex items-center gap-3"
                    >
                      <div className="w-7 h-7 rounded-lg border-2 flex items-center justify-center transition-all bg-amber-500 border-amber-500 shadow-lg shadow-amber-500/30">
                        <span className="text-white text-sm font-bold">✓</span>
                      </div>
                      <Icon className={`h-7 w-7 ${isDark ? "text-amber-400" : "text-amber-600"}`} />
                      <span className={`text-lg font-bold ${colors.textPrimary}`}>
                        {product.name}
                      </span>
                    </button>
                  </div>
                  
                  {/* Settings Row - Premium and Commission */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 mb-6">
                    {/* Premium */}
                    <div className="space-y-1 min-w-0 col-span-2 sm:col-span-1">
                      <span className={`block text-xs font-semibold uppercase tracking-wide leading-tight ${colors.textMuted}`}>Premium/mo</span>
                      <div className="relative">
                        <span className={`absolute left-3 top-1/2 -translate-y-1/2 text-base font-bold pointer-events-none ${colors.textSecondary}`}>$</span>
                        <input
                          type="number"
                          value={Math.round(product.avgPremium / 12) || ""}
                          onChange={(e) => updateProductPremium(product.id, (parseInt(e.target.value) || 0) * 12)}
                          onClick={(e) => e.stopPropagation()}
                          className={`w-full pl-7 pr-2 py-2 text-base sm:text-lg font-bold rounded-lg border-2 transition-all ${
                            isDark 
                              ? "bg-white/10 border-white/20 text-white placeholder:text-white/30 focus:border-amber-400/50"
                              : "bg-white border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-amber-500"
                          } focus:outline-none`}
                          placeholder="40"
                          min={0}
                        />
                      </div>
                    </div>
                    
                    {/* Year 1 Commission */}
                    <div className="space-y-1 min-w-0">
                      <span className={`block text-xs font-semibold uppercase tracking-wide leading-tight ${isDark ? "text-emerald-400" : "text-emerald-600"}`}>Y1 Comm %</span>
                      <input
                        type="number"
                        value={product.year1CommissionPct || ""}
                        onChange={(e) => updateProductCommission(product.id, 'year1CommissionPct', parseInt(e.target.value) || 0)}
                        onClick={(e) => e.stopPropagation()}
                        className={`w-full px-2 py-2 text-base sm:text-lg font-bold rounded-lg border-2 transition-all text-center ${
                          isDark 
                            ? "bg-white/10 border-white/20 text-white placeholder:text-white/30 focus:border-emerald-400/50"
                            : "bg-white border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-emerald-500"
                        } focus:outline-none`}
                        placeholder="20"
                        min={0}
                        max={100}
                      />
                    </div>
                    
                    {/* Renewal Commission */}
                    <div className="space-y-1 min-w-0">
                      <span className={`block text-xs font-semibold uppercase tracking-wide leading-tight ${isDark ? "text-amber-400" : "text-amber-600"}`}>Renewal %</span>
                      <input
                        type="number"
                        value={product.renewalCommissionPct || ""}
                        onChange={(e) => updateProductCommission(product.id, 'renewalCommissionPct', parseInt(e.target.value) || 0)}
                        onClick={(e) => e.stopPropagation()}
                        className={`w-full px-2 py-2 text-base sm:text-lg font-bold rounded-lg border-2 transition-all text-center ${
                          isDark 
                            ? "bg-white/10 border-white/20 text-white placeholder:text-white/30 focus:border-amber-400/50"
                            : "bg-white border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-amber-500"
                        } focus:outline-none`}
                        placeholder="15"
                        min={0}
                        max={100}
                      />
                    </div>
                  </div>

                  {/* Penetration Rate Slider - At Bottom */}
                  <div 
                    className="pt-5 border-t-2"
                    style={{ borderColor: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)" }}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 mb-3">
                      <span className={`text-xs sm:text-sm font-bold uppercase tracking-wide ${isDark ? "text-blue-400" : "text-blue-600"}`}>
                        Penetration Rate
                      </span>
                      <div className="flex items-baseline gap-2">
                        <span className={`text-2xl sm:text-3xl font-black ${isDark ? "text-blue-400" : "text-blue-600"}`}>
                          {product.penetrationRate}%
                        </span>
                        <span className={`text-xs sm:text-sm whitespace-nowrap ${colors.textMuted}`}>
                          = {policies} policies
                        </span>
                      </div>
                    </div>
                    <Slider
                      value={[product.penetrationRate]}
                      onValueChange={(v) => updateProductCommission(product.id, 'penetrationRate', v[0])}
                      min={5}
                      max={80}
                      step={5}
                      className="w-full"
                    />
                    <div className="flex justify-between mt-2">
                      <span className={`text-xs ${colors.textMuted}`}>5%</span>
                      <span className={`text-xs ${colors.textMuted}`}>80%</span>
                    </div>

                  </div>

                </motion.div>
              );
            })}
          </div>

          {/* Inactive Products - Collapsible */}
          {products.filter(p => !p.enabled).length > 0 && (
            <div className="mt-6">
              <button
                onClick={() => setShowInactiveProducts(!showInactiveProducts)}
                className={`w-full flex items-center justify-between p-4 rounded-xl transition-all ${
                  isDark 
                    ? "bg-white/5 hover:bg-white/10 border border-white/10"
                    : "bg-slate-100 hover:bg-slate-200 border border-slate-200"
                }`}
              >
                <span className={`font-semibold ${colors.textSecondary}`}>
                  {products.filter(p => !p.enabled).length} More Products Available
                </span>
                {showInactiveProducts ? (
                  <ChevronUp className={`h-5 w-5 ${colors.textMuted}`} />
                ) : (
                  <ChevronDown className={`h-5 w-5 ${colors.textMuted}`} />
                )}
              </button>
              
              <AnimatePresence>
                {showInactiveProducts && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
                      {products.filter(p => !p.enabled).map((product) => {
                        const Icon = product.icon;
                        
                        return (
                          <motion.div
                            key={product.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="p-4 rounded-xl transition-all duration-300 cursor-pointer hover:opacity-80"
                            style={{
                              background: colors.productBgInactive,
                              border: `2px solid ${colors.productBorderInactive}`,
                              opacity: 0.6,
                            }}
                            onClick={() => toggleProduct(product.id)}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center ${
                                isDark ? "border-white/30" : "border-slate-300"
                              }`} />
                              <Icon className={`h-6 w-6 ${colors.textMuted}`} />
                              <span className={`text-base font-semibold ${colors.textMuted}`}>
                                {product.name}
                              </span>
                              <span className={`ml-auto text-sm ${colors.textMuted}`}>
                                Click to add
                              </span>
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}


           {/* Per-product breakdown when something is selected */}
           {enabledProducts.length > 0 && (
             <div className="mt-6 pt-6 border-t-2 border-current/10">
               <div className="flex items-center gap-2 mb-4">
                 <ArrowRight className={`h-5 w-5 ${isDark ? "text-amber-400" : "text-amber-600"}`} />
                 <span className={`text-lg font-bold ${colors.textPrimary}`}>Revenue Breakdown</span>
               </div>
               <p className={`text-sm mb-4 ${colors.textMuted}`}>
                 Policies = clients x penetration rate. Revenue = policies x (monthly premium x 12) x commission %.
               </p>
               {/* Column headers (desktop only) */}
               <div className="hidden sm:flex items-center justify-end gap-4 mb-2 px-4">
                 <span className={`text-xs font-semibold uppercase tracking-wider w-20 text-right ${colors.textMuted}`}>Policies</span>
                 <span className={`text-xs font-semibold uppercase tracking-wider w-28 text-right ${isDark ? "text-amber-400/70" : "text-amber-600/70"}`}>Year 1</span>
                 <span className={`text-xs font-semibold uppercase tracking-wider w-28 text-right ${isDark ? "text-emerald-400/70" : "text-emerald-600/70"}`}>Renewal</span>
               </div>
               <div className="space-y-3">
                 {calculations.productRevenues.map(product => (
                   <div 
                     key={product.id}
                     className="py-3 px-3 sm:px-4 rounded-xl sm:flex sm:items-center sm:justify-between"
                     style={{ 
                       background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)",
                       border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
                     }}
                   >
                     <div className="flex items-center gap-3 min-w-0 flex-1 mb-2 sm:mb-0">
                       <product.icon className={`h-5 w-5 flex-shrink-0 ${isDark ? "text-amber-400" : "text-amber-600"}`} />
                       <span className={`text-sm sm:text-base font-semibold truncate ${colors.textPrimary}`}>{product.name}</span>
                     </div>
                     <div className="grid grid-cols-3 gap-2 sm:flex sm:items-center sm:gap-4">
                       <div className="min-w-0">
                         <span className={`sm:hidden block text-[10px] font-semibold uppercase tracking-wider ${colors.textMuted}`}>Policies</span>
                         <span className={`block text-sm sm:w-20 sm:text-right truncate ${colors.textMuted}`}>
                           {product.policies}
                         </span>
                       </div>
                       <div className="min-w-0">
                         <span className={`sm:hidden block text-[10px] font-semibold uppercase tracking-wider ${isDark ? "text-amber-400/70" : "text-amber-600/70"}`}>Year 1</span>
                         <span className={`block text-base sm:text-lg font-bold sm:w-28 sm:text-right truncate ${isDark ? "text-amber-400" : "text-amber-600"}`}>
                           {formatCurrency(product.year1Revenue)}
                         </span>
                       </div>
                       <div className="min-w-0">
                         <span className={`sm:hidden block text-[10px] font-semibold uppercase tracking-wider ${isDark ? "text-emerald-400/70" : "text-emerald-600/70"}`}>Renewal</span>
                         <span className={`block text-base sm:text-lg font-bold sm:w-28 sm:text-right truncate ${isDark ? "text-emerald-400" : "text-emerald-600"}`}>
                           {formatCurrency(product.renewalRevenue)}
                         </span>
                       </div>
                     </div>
                   </div>
                 ))}
                 {/* Totals row */}
                 <div 
                   className="py-3 px-3 sm:px-4 rounded-xl mt-1 sm:flex sm:items-center sm:justify-between"
                   style={{ 
                     background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
                     border: `2px solid ${isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.15)"}`,
                   }}
                 >
                   <span className={`block text-sm sm:text-base font-bold mb-2 sm:mb-0 ${colors.textPrimary}`}>Total</span>
                   <div className="grid grid-cols-3 gap-2 sm:flex sm:items-center sm:gap-4">
                     <span className={`text-sm font-bold sm:w-20 sm:text-right truncate ${colors.textSecondary}`}>
                       {calculations.totalPolicies}
                     </span>
                     <span className={`text-base sm:text-lg font-black sm:w-28 sm:text-right truncate ${isDark ? "text-amber-400" : "text-amber-600"}`}>
                       {formatCurrency(calculations.totalYear1Revenue)}
                     </span>
                     <span className={`text-base sm:text-lg font-black sm:w-28 sm:text-right truncate ${isDark ? "text-emerald-400" : "text-emerald-600"}`}>
                       {formatCurrency(calculations.totalRenewalRevenue)}
                     </span>
                   </div>
                 </div>
               </div>

             </div>
           )}
        </CalculatorCard>
      </motion.div>

      {/* Quick Start Presets */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="mt-10 flex flex-wrap justify-center gap-4"
      >
        <button
          onClick={resetAll}
          className={`px-6 py-4 rounded-xl text-lg font-bold transition-all transform hover:scale-105 flex items-center gap-3 ${
            isDark 
              ? "bg-red-500/20 text-red-300 border-2 border-red-500/40 hover:bg-red-500/30"
              : "bg-red-100 text-red-700 border-2 border-red-300 hover:bg-red-200"
          }`}
        >
          <RotateCcw size={20} />
          Reset All
        </button>
        <button
          onClick={() => {
            setTotalClients(100);
            setProducts(defaultProducts.map(p => ({
              ...p,
              enabled: ["dental", "vision"].includes(p.id),
              penetrationRate: 15,
              year1CommissionPct: 15,
              renewalCommissionPct: 10
            })));
          }}
          className={`px-6 py-4 rounded-xl text-lg font-bold transition-all transform hover:scale-105 flex items-center gap-3 ${
            isDark 
              ? "bg-white/10 text-white/80 border-2 border-white/20 hover:bg-white/15"
              : "bg-slate-100 text-slate-700 border-2 border-slate-200 hover:bg-slate-200"
          }`}
        >
          <Zap size={20} />
          Conservative Start
        </button>
        <button
          onClick={() => {
            setTotalClients(150);
            setProducts(defaultProducts.map(p => ({
              ...p,
              enabled: ["dental", "vision", "hospital", "accident"].includes(p.id)
            })));
          }}
          className="px-8 py-4 rounded-xl text-lg font-bold transition-all transform hover:scale-105 flex items-center gap-3 shadow-xl"
          style={{
            background: "linear-gradient(135deg, #fbbf24, #f59e0b)",
            color: "#0a1628",
            boxShadow: "0 10px 40px -10px rgba(251,191,36,0.5)",
          }}
        >
          <Zap size={20} />
          Typical Book
        </button>
        <button
          onClick={() => {
            setTotalClients(300);
            setProducts(defaultProducts.map(p => ({ 
              ...p, 
              enabled: true,
              penetrationRate: Math.min(p.penetrationRate + 10, 50)
            })));
          }}
          className={`px-6 py-4 rounded-xl text-lg font-bold transition-all transform hover:scale-105 flex items-center gap-3 ${
            isDark 
              ? "bg-emerald-500/30 text-emerald-300 border-2 border-emerald-500/50 hover:bg-emerald-500/40 shadow-xl shadow-emerald-500/20"
              : "bg-emerald-100 text-emerald-700 border-2 border-emerald-400 hover:bg-emerald-200 shadow-lg shadow-emerald-500/20"
          }`}
        >
          <Zap size={20} />
          Full Potential
        </button>
      </motion.div>
    </div>
  );
};

const CrossSellSimple = () => {
  return (
    <>
      <Seo
        title="Cross-Sell Revenue Calculator for Health Insurance Brokers | Chirpwell"
        description="Model ancillary cross-sell revenue for your book: penetration rates, average monthly premiums, and first-year vs. renewal commissions. Free broker tool."
        path="/cross-sell-simple"
      />
      <CalculatorLayout
        title="Cross-Sell Revenue Calculator"
        subtitle="Simple revenue projection for your client book"
        icon={TrendingUp}
        badge="GAP Analysis"
      >
        <CrossSellSimpleContent />
      </CalculatorLayout>
    </>
  );
};

export default CrossSellSimple;

